import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useNavigate,
  useLocation,
  useSearchParams,
} from "react-router-dom";

import { useAppSelector, useAppDispatch } from "store/index";
import {
  deletePaymentProvider,
  getPaymentProvidersList,
  handlePaginate,
  handleSelectAll,
  handleSelectItem,
  PaymentProvider,
  updatePaymentProvidersBunch,
} from "store/payments/providers/slice";
import { loyaltyListLimits } from "configs/limits";
import { ModalTypes } from "configs/types";
import { openModal } from "store/modals/slice";

import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import Pagination from "components/Pagination";
import ActionBtn from "components/buttons/ActionBtn";
import ProvidersFilter from "./ProvidersFilter";

import {
  NotificationTypes,
  showNotification,
} from "../../../../store/notifications/slice";
import { Table } from "components/table/Table";
import EmptyState from "components/EmptyState/EmptyState";
import DataTableSkeleton from "components/global/Skeleton/TableSkeleton/DataTableSkeleton";
import PaginationSkeleton from "components/global/Skeleton/PaginationSkeleton/PaginationSkeleton";
import { usePermissionHelper } from "helpers/usePermissionHelper";
import { RefreshButton } from "components/buttons/RefreshButton";
import { useRefreshList } from "helpers/useRefreshList";

const PaymentProvidersList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const permissions = usePermissionHelper();
  const canCreate = permissions?.create ?? false;
  const canUpdate = permissions?.update ?? false;
  const canDelete = permissions?.delete ?? false;
  const canRead = permissions?.read ?? false;
  const activeProviderId = searchParams.get("entityId");

  const [order, setOrder] = useState<{
    title: string;
    isActive: string;
    id: string;
  }>({
    title: "none",
    isActive: "none",
    id: "none",
  });
  const [currentLimit, setLimit] = useState<number>(loyaltyListLimits[0]);
  const [search, setSearch] = useState<string>("");
  const [active, setActive] = useState<
    { value: number | string; label: string }[]
  >([{ value: 3, label: "All" }]);

  const options = [
    {
      label: t("activate"),
      value: "activate",
    },
    {
      label: t("hide"),
      value: "hide",
    },
  ];

  const list = useAppSelector((store) => store["payment-providers"].list);
  const isListFetched = useAppSelector(
    (store) => store["payment-providers"].isListFetched,
  );
  const totalItems = useAppSelector(
    (store) => store["payment-providers"].totalItems,
  );
  const totalPages = useAppSelector(
    (store) => store["payment-providers"].totalPages,
  );
  const activePage = useAppSelector(
    (store) => store["payment-providers"].activePage,
  );
  const selectedItems = useAppSelector(
    (store) => store["payment-providers"].selectedItems,
  );
  const processingItemId = useAppSelector(
    (store) => store["payment-providers"].processingItemId,
  );
  const { refresh, isRefreshRequested } = useRefreshList({
    sliceName: 'payment-providers',
    isListFetched
  });
  

  useEffect(() => {
    if (!isListFetched) {
      dispatch(
        getPaymentProvidersList({
          limit: currentLimit,
          offset: activePage - 1,
          title: search,
          isActive:
            active[0] && active[0].value !== 3
              ? (active[0].value as number)
              : undefined,
          order,
        }),
      );
    }
  }, [isListFetched, activePage, currentLimit, order]);

  const handleAllClick = () => {
    dispatch(handleSelectAll());
  };
  const handleItemClick = (id: number) => {
    dispatch(handleSelectItem(id));
  };
  const handlePrevClick = () => {
    if (activePage > 1) {
      dispatch(handlePaginate(activePage - 1));
    }
  };
  const handleNextClick = () => {
    if (activePage !== totalPages) {
      dispatch(handlePaginate(activePage + 1));
    }
  };

  const handleActionClick = async (e: string) => {
    if (!canUpdate) return;

    const result = await dispatch(
      updatePaymentProvidersBunch({
        ids: selectedItems,
        isActive: e === "activate" ? 1 : 0,
      }),
    );

    if (result.payload !== true) {
      dispatch(
        showNotification({
          title: t("error"),
          message: t("unknownError"),
          type: NotificationTypes.error,
        }),
      );
    }
  };

  const handleOpenEditModal = (id: number) => {
    navigate(
      `${location.pathname}?activeModal=${ModalTypes.paymentProvider}&entityId=${id}`,
    );
    dispatch(openModal({ activeModal: ModalTypes.paymentProvider }));
  };
  const handleOpenCreateModal = () => {
    navigate(`${location.pathname}?activeModal=${ModalTypes.paymentProvider}`);
    dispatch(openModal({ activeModal: ModalTypes.paymentProvider }));
  };

  const handleOrderClick = (key: keyof typeof order) => {
    const current = order[key];
    let next: "asc" | "desc" | "none";

    if (current === "none") next = "asc";
    else if (current === "asc") next = "desc";
    else next = "none";

    const newOrder = {
      [key]: next,
      ...Object.fromEntries(Object.entries(order).filter(([k]) => k !== key)),
    };
    // @ts-ignore
    setOrder(newOrder);
    dispatch(handlePaginate(1));
  };

  const handleDeleteItem = async (id: number) => {
    if (!canDelete) return;

    const resultTag = await dispatch(deletePaymentProvider(id ?? 0));
    if (resultTag?.payload === true) {
      dispatch(
        showNotification({
          title: t("success"),
          message: t("entityDeleteSuccess"),
          type: NotificationTypes.success,
        }),
      );
    } else {
      dispatch(
        showNotification({
          title: t("error"),
          message: t("entityDeleteError"),
          type: NotificationTypes.error,
        }),
      );
    }
  };

  return (
    <div className="page-list-wrapper page-list--with-filter">
      <ProvidersFilter
        handleApply={() => {
          dispatch(handlePaginate(1));
        }}
        searchValue={search}
        handleSearch={setSearch}
        selectedActive={active}
        handleUpdateActive={setActive}
      />
      <div className="page-list--with-filter-content">
        <div className="page-list-header">
          <div className="page-list-header--box">
            {canCreate && (
              <ActionBtn
                txt={t("addProvider")}
                handleClick={() => {
                  handleOpenCreateModal();
                }}
              />
            )}
          </div>
          <div className="page-list-header--box page-list-header--box--right">
            <RefreshButton
              position="right"
              disabled={isRefreshRequested}
              isLoading={isRefreshRequested}
              onClick={() => refresh()}
            />
          </div>
          {canUpdate && (
            <div className="page-list-header--box">
              <DefaultDropdown
                onSelect={(e) => {
                  handleActionClick(e as string);
                }}
                placeholder={t("chooseAction")}
                options={options}
                isSmall
                disabled={selectedItems.length === 0}
              />
            </div>
          )}
        </div>

        {!isListFetched && (
          <>
            <DataTableSkeleton columnsCount={6} rowsCount={10} />
            <PaginationSkeleton />
          </>
        )}

        {isListFetched && (
          <div className="game-table-wrap">
            {list.length === 0 && (
              <EmptyState message={t("noPaymentProvidersFound")} />
            )}

            {list.length > 0 && (
              <Table
                data={list as Array<PaymentProvider & { id: number }>}
                idField="id"
                columns={{
                  sortingColumns: {
                    isActive: {
                      value: t("active"),
                      hasSorting: true,
                    },
                  },
                }}
                order={order}
                selectedItems={selectedItems}
                hasCheckbox={canUpdate}
                hasEdit={canUpdate}
                hasView={canRead && !canUpdate}
                hasDelete={canDelete}
                hasOptions={canRead || canUpdate || canDelete}
                onAllSelect={handleAllClick}
                onSelect={(id) => handleItemClick(id)}
                onDelete={canDelete ? (id) => handleDeleteItem(id) : undefined}
                onEdit={canUpdate ? (id) => handleOpenEditModal(id) : undefined}
                onView={
                  canRead && !canUpdate
                    ? (id) => handleOpenEditModal(id)
                    : undefined
                }
                onSort={(id) => (id ? handleOrderClick(id) : {})}
                activeRowId={activeProviderId}
                processingId={processingItemId}
              />
            )}
          </div>
        )}

        {isListFetched && list.length > 0 && (
          <Pagination
            handleLimitClick={(e) => {
              setLimit(e);
              dispatch(handlePaginate(1));
            }}
            limitsArray={loyaltyListLimits}
            activePage={activePage}
            limit={currentLimit}
            totalItems={totalItems}
            totalPages={totalPages}
            handleNextClick={handleNextClick}
            handlePrevClick={handlePrevClick}
          />
        )}
      </div>
    </div>
  );
};

export default PaymentProvidersList;
